import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { WorkspaceProgressStep } from '@/lib/types/rome';
import { cn } from '@/lib/utils/cn';

// Step keys that are NOT part of the startup progress bar (lifecycle events)
const LIFECYCLE_STEPS = new Set(['running', 'stopping', 'stopped', 'failed']);

// Known Rome-led step keys in the order they occur during startup
// This is a fixed denominator so the bar only ever goes up
const KNOWN_ROME_STEPS = ['storage', 'network', 'domain', 'deploy', 'scheduling', 'pulling', 'initializing'];

// Percentage of bar width allocated to Rome-led steps (provisioning + K8s)
const ROME_STEPS_PERCENT = 70;

// Percentage of bar width allocated to pod-led steps
const POD_STEPS_PERCENT = 30;

// Fixed increment per completed Rome step (70 / 7 = 10%)
const ROME_STEP_INCREMENT = ROME_STEPS_PERCENT / KNOWN_ROME_STEPS.length;

interface StartupProgressBarProps {
  steps: WorkspaceProgressStep[];
  className?: string;
}

/**
 * Compute the progress bar fill percentage from startup steps.
 *
 * The bar is split into two logical sections:
 *   - 70% for Rome-led steps (provisioning and K8s scheduling/pulling/init)
 *   - 30% for pod-led steps (services, etc. declared by the pod)
 *
 * Rome-led steps use a fixed denominator of 7 (the known step set) so the
 * bar only ever increases as steps complete. Each completed Rome step adds
 * exactly 10% (70/7). Pod-led steps use the total declared by the pod.
 *
 * For older antioch-sim versions with no pod steps, the bar reaches 70%
 * when "initializing" completes, then the state transitions to RUNNING.
 *
 * @param steps - The progress steps from the API.
 * @return The fill percentage (0-100) and the current in-progress step.
 */
export function computeProgress(steps: WorkspaceProgressStep[]): {
  percent: number;
  currentStep: WorkspaceProgressStep | null;
} {
  // Filter out lifecycle events
  const startupSteps = steps.filter((s) => !LIFECYCLE_STEPS.has(s.step));

  if (startupSteps.length === 0) {
    return { percent: 0, currentStep: null };
  }

  // Build a set of completed step keys for fast lookup
  const completedKeys = new Set(startupSteps.filter((s) => s.status === 'completed').map((s) => s.step));

  // Rome-led progress: each known step that is completed adds a fixed 10%
  let romePercent = 0;
  for (const key of KNOWN_ROME_STEPS) {
    if (completedKeys.has(key)) {
      romePercent += ROME_STEP_INCREMENT;
    }
  }

  // Pod-led progress: completed out of total declared pod steps
  const podSteps = startupSteps.filter((s) => s.step.startsWith('pod_'));
  let podPercent = 0;
  if (podSteps.length > 0) {
    const podCompleted = podSteps.filter((s) => s.status === 'completed').length;
    podPercent = (podCompleted / podSteps.length) * POD_STEPS_PERCENT;
  }

  // Find the current in-progress step, or fall back to the last completed step
  // (covers the brief gap between auto-completing one step and appending the next)
  const reversed = [...startupSteps].reverse();
  const currentStep =
    reversed.find((s: WorkspaceProgressStep) => s.status === 'in_progress') ??
    reversed.find((s: WorkspaceProgressStep) => s.status === 'completed') ??
    null;

  return { percent: romePercent + podPercent, currentStep };
}

/**
 * Compact inline progress bar for workspace startup.
 *
 * Displays a horizontal bar split 70/30 between Rome-led and pod-led steps.
 * Shows the current startup step message on hover via tooltip.
 * Animated with a moving stripe pattern to indicate active progress.
 */
export function StartupProgressBar({ steps, className }: StartupProgressBarProps) {
  const { percent, currentStep } = computeProgress(steps);
  const tooltipText = currentStep?.message ?? 'Starting...';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'relative h-2.5 w-full min-w-[100px] max-w-[180px] cursor-pointer overflow-hidden rounded-full bg-gray-200 dark:bg-white/10',
            className
          )}
        >
          {/* Fill bar - always present so CSS transition works when width changes */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-[1500ms] ease-out"
            style={{
              width: `${Math.min(percent, 100)}%`,
              backgroundImage:
                'linear-gradient(90deg, rgba(59,130,246,1) 0%, rgba(96,165,250,1) 50%, rgba(59,130,246,1) 100%)',
              backgroundSize: '200% 100%',
              animation: 'progress-shimmer 2s ease-in-out infinite',
            }}
          />

          {/* Indeterminate shimmer overlay while waiting for data */}
          {percent === 0 && (
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.3) 40%, rgba(59,130,246,0.3) 60%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'progress-shimmer 2s ease-in-out infinite',
              }}
            />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p className="text-xs">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}
